import type { PackageResponse } from '../../types/packages';
import { formatAed, formatDate } from '../offers/offerUtils';
import OrderCell from '../services/OrderCell';
import { RUNNING_STATUSES, packageRowActions, packageStatusChipClass, type PackageRowAction } from './packageUtils';
import { reasonText, type PackagesT } from './packagesI18n';

export interface PackagesTableProps {
  t: PackagesT;
  lang: 'en' | 'ar';
  /** Running packages first, in display order (sortForTable). */
  packages: PackageResponse[];
  archived: boolean;
  /** Order changes need the whole running set on screen (no status filter, one page). */
  reorderEnabled: boolean;
  reordering: boolean;
  onOpen: (id: string) => void;
  onAction: (pkg: PackageResponse, action: PackageRowAction) => void;
  onMove: (id: string, position: number) => void;
}

/** Spec 081b — the Packages tab table. Running packages carry a position control (garage profile order). */
export default function PackagesTable(props: PackagesTableProps) {
  const { t, lang, packages, archived, reorderEnabled, reordering, onOpen, onAction, onMove } = props;
  const running = packages.filter((p) => RUNNING_STATUSES.includes(p.status));

  return (
    <div className="partner-services-table-wrapper offer-table-wrapper">
      <table className="partner-services-table offer-table pkg-table">
        <thead>
          <tr>
            <th className="partner-services-order-col">{t('packages.col.order')}</th>
            <th>{t('packages.col.name')}</th>
            <th>{t('packages.col.price')}</th>
            <th>{t('packages.col.status')}</th>
            <th>{t('packages.col.window')}</th>
            <th>{t('packages.col.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {packages.map((p) => {
            const { edit, actions } = packageRowActions(p, archived);
            const name = (lang === 'ar' && p.nameAr) ? p.nameAr : p.nameEn;
            const position = running.findIndex((r) => r.id === p.id) + 1;
            return (
              <tr key={p.id} data-testid={`pkg-row-${p.id}`}>
                <td className="partner-services-order-col">
                  {position > 0 && (
                    <OrderCell position={position} total={running.length} disabled={!reorderEnabled || reordering}
                      disabledHint={reorderEnabled ? undefined : t('packages.reorder.disabledHint')} onMove={(to) => onMove(p.id, to)} />
                  )}
                </td>
                <td>
                  <button type="button" className="partner-services-name-link offer-link" onClick={() => onOpen(p.id)}>{name || '—'}</button>
                  <div className="offer-muted pkg-table__items">{t('packages.itemsCount', { n: p.items?.length ?? 0 })}</div>
                </td>
                <td className="offer-nowrap">
                  <div className="pkg-table__mode">{t(`packages.mode.${p.pricingMode}`)}</div>
                  <span dir="ltr">
                    {p.pricingMode === 'FIXED'
                      ? <>{p.originalTotal != null && <s className="pkg-struck">{formatAed(p.originalTotal)}</s>} {formatAed(p.packagePrice)}</>
                      : t('packages.price.percentOff', { pct: p.discountPercent ?? 0 })}
                  </span>
                </td>
                <td>
                  <span className={packageStatusChipClass(p.status)}>{t(`packages.status.${p.status}`)}</span>
                  {p.unavailableReason && p.status !== 'ENDED' && (
                    <div className="pkg-badge pkg-badge--warn" data-testid={`pkg-unavailable-${p.id}`} title={reasonText(t, p.unavailableReason)}>
                      {t('packages.unavailable.badge')}: {reasonText(t, p.unavailableReason)}
                    </div>
                  )}
                </td>
                <td className="offer-nowrap">
                  {p.startsAt || p.endsAt
                    ? <span dir="ltr">{formatDate(p.startsAt, lang)} – {formatDate(p.endsAt, lang)}</span>
                    : <span className="offer-muted">{t('packages.window.always')}</span>}
                </td>
                <td>
                  <div className="offer-row-actions">
                    <button type="button" className="offer-btn offer-btn--small" onClick={() => onOpen(p.id)}>
                      {edit ? t('packages.action.edit') : t('packages.action.view')}
                    </button>
                    {actions.map((a) => (
                      <button key={a} type="button" onClick={() => onAction(p, a)}
                        className={`offer-btn offer-btn--small${a === 'end' || a === 'archive' ? ' offer-btn--danger-ghost' : ' offer-btn--ghost'}`}>
                        {t(`packages.action.${a}`)}
                      </button>
                    ))}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
